<div class="cat-assign">
  <!-- ── Header ─────────────────────────────────────────────────────────── -->
  <header class="cat-assign__head">
    <div class="cat-assign__head-text">
      <p class="cat-assign__subtitle">
        Add navigation shelves to the selected prices. Existing placements are
        kept — this only adds.
      </p>
    </div>
    @if (channel) {
    <span class="cat-assign__scope" title="Binding is scoped to this channel">
      <mat-icon aria-hidden="true">hub</mat-icon>
      {{ channel }}
    </span>
    }
  </header>

  <section class="cat-assign__targets" aria-label="Selected prices">
    <div class="cat-assign__targets-head">
      <mat-icon aria-hidden="true">sell</mat-icon>
      <strong>{{ priceCount() }}</strong>
      price{{ priceCount() === 1 ? "" : "s" }} from {{ packageCount() }} product
      offering{{ packageCount() === 1 ? "" : "s" }}
    </div>
    <ul class="cat-assign__target-chips">
      @for (row of visibleTargets; track trackByPriceId($index, row)) {
      <li
        class="cat-assign__target-chip"
        [title]="row.title + ' · ' + (row.offeringName || row.offeringId)"
      >
        {{ row.title }}
      </li>
      } @if (hiddenTargetCount > 0) {
      <li class="cat-assign__target-chip cat-assign__target-chip--more">
        +{{ hiddenTargetCount }} more
      </li>
      }
    </ul>
  </section>

  <!-- easyApp derives a price's shelf link from its category / subCategory
       names and replaces the link set on every save, so a per-price shelf
       write there persists nothing. Say so and disable Apply — a 200 that
       changed nothing is worse than a blocked button. -->
  @if (isEasyAppChannel()) {
  <div class="cat-assign__blocked" role="note">
    <mat-icon aria-hidden="true">block</mat-icon>
    <div>
      <strong>Not available on {{ channel }}</strong>
      <p>
        This channel files a price under a shelf through its Category /
        SubCategory fields, so shelves picked here would not be saved. Open the
        price in the editor to change its categories.
      </p>
    </div>
  </div>
  }

  <!-- ── Catalog picker ─────────────────────────────────────────────────── -->
  <section class="cat-assign__catalog">
    <!-- Catalog selector — shown on every channel. Regular categories are
         browsed catalog-first (like the editor's Shelf tab): pick a catalog,
         the tree/picker below is scoped to it. Single-catalog channels show the
         name as a caption (a 1-item dropdown is pointless). -->
    <div class="cat-assign__catalog-pick">
      <span class="cat-assign__catalog-label">Catalog</span>
      @switch (catalogLoadState()) { @case ("loading") {
      <span class="cat-assign__catalog-only">
        <span class="cat-assign__spinner" aria-hidden="true"></span>
        Loading catalogs…
      </span>
      } @case ("error") {
      <span class="cat-assign__catalog-only">
        <mat-icon aria-hidden="true">error_outline</mat-icon>
        Couldn't load catalogs
      </span>
      } @default { @if (catalogs().length > 1) {
      <enr-select
        size="small"
        aria-label="Select catalog"
        [options]="catalogOptions()"
        [searchable]="true"
        [value]="selectedCatalogId()"
        (valueChange)="onSelectCatalog($any($event))"
      ></enr-select>
      } @else if (catalogs().length === 1) {
      <span class="cat-assign__catalog-only">
        {{ catalogLabel(catalogs()[0]) }}
        <code class="cat-assign__catalog-id">{{ catalogs()[0].id }}</code>
      </span>
      } @else {
      <span class="cat-assign__catalog-only">No catalogs for this channel</span>
      } } }
    </div>

    <!-- Mode toggle — hint on the left, segmented toggle on the right, one row
         (mirrors the editor's "Add shelves" layout). -->
    <div class="cat-assign__modes-row">
      <p class="cat-assign__hint">{{ activeModeOption().hint }}</p>
      <div class="cat-assign__modes" role="tablist" aria-label="Pick a mode">
        @for (m of modes; track trackByMode($index, m)) {
        <button
          type="button"
          role="tab"
          class="cat-assign__mode"
          [class.cat-assign__mode--active]="activeMode() === m.id"
          [attr.aria-selected]="activeMode() === m.id"
          (click)="setMode(m.id)"
        >
          <mat-icon class="cat-assign__mode-icon" aria-hidden="true">
            {{ m.icon }}
          </mat-icon>
          <span>{{ m.label }}</span>
        </button>
        }
      </div>
    </div>

    <!-- Category-type filter — AI Chatbot only. Switches the browsed feed
         between the catalog (regular) categories and the separate LifeStyle
         characteristic feed; applies to both Search and Tree modes. -->
    @if (isAiChatbotChannel()) {
    <div
      class="cat-assign__type-filter"
      role="group"
      aria-label="Filter categories by type"
    >
      <span class="cat-assign__type-filter-label">Category type</span>
      <div class="cat-assign__type-seg">
        @for (opt of typeFilterOptions; track opt.id) {
        <button
          type="button"
          class="cat-assign__type-opt"
          [class.cat-assign__type-opt--active]="typeFilter() === opt.id"
          [class.cat-assign__type-opt--life]="opt.id === 'lifestyle'"
          [attr.aria-pressed]="typeFilter() === opt.id"
          (click)="setTypeFilter(opt.id)"
        >
          @if (opt.icon) {
          <mat-icon aria-hidden="true">{{ opt.icon }}</mat-icon>
          } {{ opt.label }}
        </button>
        }
      </div>
    </div>
    }

    <!-- Load state -->
    @switch (loadState()) { @case ("loading") {
    <div class="cat-assign__stub">
      <span class="cat-assign__spinner" aria-hidden="true"></span>
      <div class="cat-assign__stub-title">Loading catalog…</div>
    </div>
    } @case ("error") {
    <div class="cat-assign__stub cat-assign__stub--error">
      <mat-icon aria-hidden="true">error_outline</mat-icon>
      <div class="cat-assign__stub-title">Couldn't load the catalog</div>
      <div class="cat-assign__stub-hint">{{ loadError() }}</div>
      <button
        type="button"
        class="cat-assign__retry"
        (click)="retryLoadCatalog()"
      >
        <mat-icon aria-hidden="true">refresh</mat-icon>
        Try again
      </button>
    </div>
    } @case ("ready") { @if (catalogCount() === 0) {
    <div class="cat-assign__stub">
      <mat-icon aria-hidden="true">inbox</mat-icon>
      <div class="cat-assign__stub-title">Catalog is empty</div>
      <div class="cat-assign__stub-hint">
        No categories exist for this channel yet.
      </div>
    </div>
    } @else { @switch (activeMode()) {

    <!-- ── Search (picker) mode ── -->
    @case ("picker") {
    <div class="cat-assign__picker">
      <label class="cat-assign__search">
        <mat-icon class="cat-assign__search-icon" aria-hidden="true">
          search
        </mat-icon>
        <input
          type="text"
          class="cat-assign__search-input"
          placeholder="Search categories by name, id, or parent…"
          [value]="searchQuery()"
          (input)="onSearchChange($any($event.target).value)"
        />
        @if (searchQuery()) {
        <button
          type="button"
          class="cat-assign__search-clear"
          aria-label="Clear search"
          (click)="clearSearch()"
        >
          <mat-icon aria-hidden="true">close</mat-icon>
        </button>
        }
      </label>

      <div class="cat-assign__results-meta">
        @if (pickerHasResults()) { Showing
        <strong>{{ pickerResults().length }}</strong> of
        <strong>{{ pickerMatchCount() }}</strong> categories @if
        (pickerOverflowCount() > 0) {
        <span class="cat-assign__results-overflow">
          — narrow your search to see {{ pickerOverflowCount() }} more
        </span>
        } } @else {
        <span class="cat-assign__results-overflow">No categories match</span>
        }
      </div>

      @if (pickerHasResults()) {
      <ul class="cat-assign__results">
        @for (e of pickerResults(); track trackByEntryId($index, e)) {
        <li
          class="cat-assign__result"
          [class.cat-assign__result--selected]="isSelected(e.id)"
          [class.cat-assign__result--lifestyle]="isLifeStyle(e.id)"
        >
          <label class="cat-assign__result-main">
            <!-- Same custom check-box the Tree mode uses, so Picker and Tree
                 read identically (and we avoid a raw native input). Picker
                 rows are binary, so only the checked state is surfaced. -->
            <span class="cat-assign__tree-check cat-assign__result-check">
              <input
                type="checkbox"
                [checked]="isSelected(e.id)"
                (change)="toggleEntry(e)"
                [attr.aria-label]="'Add ' + e.name"
              />
              <span
                class="cat-assign__check-box"
                [class.cat-assign__check-box--checked]="isSelected(e.id)"
                aria-hidden="true"
              >
                @if (isSelected(e.id)) {
                <mat-icon>check</mat-icon>
                }
              </span>
            </span>
            <span class="cat-assign__result-body">
              <!-- Parent breadcrumb first so the card reads top-down
                   parent → child (e.g. "Main Package" then "↳ Best seller"). -->
              @if (e.pathLabel) {
              <span class="cat-assign__result-path" [title]="e.pathLabel">
                {{ e.pathLabel }}
              </span>
              }
              <span class="cat-assign__result-name-row">
                @if (e.pathLabel) {
                <mat-icon class="cat-assign__result-child-ic" aria-hidden="true"
                  >subdirectory_arrow_right</mat-icon
                >
                }
                <span class="cat-assign__result-name">{{ e.name }}</span>
                @if (isLifeStyle(e.id)) {
                <span class="cat-assign__life-badge" title="LifeStyle category">
                  <mat-icon aria-hidden="true">auto_awesome</mat-icon>
                  LifeStyle
                </span>
                } @if (isBoundEverywhere(e.id)) {
                <span
                  class="cat-assign__onall"
                  title="Already on every selected price"
                >
                  on all
                </span>
                }
              </span>
              <code class="cat-assign__result-id">{{ e.id }}</code>
            </span>
          </label>
        </li>
        }
      </ul>
      } @else {
      <div class="cat-assign__no-results">
        <mat-icon aria-hidden="true">search_off</mat-icon>
        <span>
          No categories match @if (searchQuery()) {
          <strong>"{{ searchQuery() }}"</strong>
          }
        </span>
        @if (searchQuery()) {
        <button type="button" class="cat-assign__link" (click)="clearSearch()">
          Clear search
        </button>
        }
      </div>
      }
    </div>
    }

    <!-- ── Tree mode ── -->
    @case ("tree") {
    <div class="cat-assign__tree-wrap">
      <div class="cat-assign__tree-toolbar">
        <span class="cat-assign__tree-meta">
          <strong>{{ treeRootCount() }}</strong> root {{ treeRootCount() === 1 ?
          "category" : "categories" }}
        </span>
        <div class="cat-assign__tree-actions">
          <button
            type="button"
            class="cat-assign__tree-action"
            (click)="expandAll()"
          >
            <mat-icon aria-hidden="true">unfold_more</mat-icon>
            Expand all
          </button>
          <button
            type="button"
            class="cat-assign__tree-action"
            (click)="collapseAll()"
          >
            <mat-icon aria-hidden="true">unfold_less</mat-icon>
            Collapse all
          </button>
        </div>
      </div>

      @if (filteredTree().length) {
      <ul class="cat-assign__tree" role="tree">
        @for (root of filteredTree(); track trackByNodeId($index, root)) {
        <ng-container
          *ngTemplateOutlet="treeNode; context: { $implicit: root, depth: 0 }"
        ></ng-container>
        }
      </ul>
      } @else {
      <div class="cat-assign__no-results">
        <mat-icon aria-hidden="true">filter_alt_off</mat-icon>
        <span>No categories match this filter</span>
      </div>
      }

      <ng-template #treeNode let-node let-depth="depth">
        @let state = nodeSelectState(node); @let expanded = isExpanded(node.id);
        @let hasChildren = (node.subCategory?.length ?? 0) > 0; @let isLife =
        isLifeStyleNode(node);
        <li
          class="cat-assign__tree-node"
          role="treeitem"
          [attr.aria-expanded]="hasChildren ? expanded : null"
          [attr.aria-selected]="state === 'checked'"
        >
          <div
            class="cat-assign__tree-row"
            [class.cat-assign__tree-row--checked]="state === 'checked'"
            [class.cat-assign__tree-row--lifestyle]="isLife"
            [style.padding-left.px]="10 + depth * 18"
          >
            @if (hasChildren) {
            <button
              type="button"
              class="cat-assign__chevron"
              [class.cat-assign__chevron--open]="expanded"
              [attr.aria-label]="(expanded ? 'Collapse ' : 'Expand ') + resolveName(node)"
              (click)="toggleExpand(node.id)"
            >
              <mat-icon aria-hidden="true">chevron_right</mat-icon>
            </button>
            } @else {
            <span
              class="cat-assign__chevron cat-assign__chevron--leaf"
              aria-hidden="true"
            ></span>
            }

            <label class="cat-assign__tree-check">
              <input
                type="checkbox"
                [checked]="state === 'checked'"
                [indeterminate]="state === 'indeterminate'"
                (change)="onToggleNode(node, $any($event.target).checked)"
                [attr.aria-label]="'Add ' + resolveName(node)"
              />
              <span
                class="cat-assign__check-box"
                [class.cat-assign__check-box--checked]="state === 'checked'"
                [class.cat-assign__check-box--indeterminate]="state === 'indeterminate'"
                aria-hidden="true"
              >
                @if (state === "checked") {
                <mat-icon>check</mat-icon>
                } @else if (state === "indeterminate") {
                <span class="cat-assign__check-dash"></span>
                }
              </span>
            </label>

            <span class="cat-assign__tree-name">{{ resolveName(node) }}</span>
            @if (isLife) {
            <span class="cat-assign__life-badge" title="LifeStyle category">
              <mat-icon aria-hidden="true">auto_awesome</mat-icon>
              LifeStyle
            </span>
            } @if (isBoundEverywhere(node.id)) {
            <span
              class="cat-assign__onall"
              title="Already on every selected price"
            >
              on all
            </span>
            }
            <code class="cat-assign__tree-id">{{ node.id }}</code>
          </div>

          @if (hasChildren && expanded) {
          <ul class="cat-assign__tree-children" role="group">
            @for (child of node.subCategory; track trackByNodeId($index, child))
            {
            <ng-container
              *ngTemplateOutlet="treeNode; context: { $implicit: child, depth: depth + 1 }"
            ></ng-container>
            }
          </ul>
          }
        </li>
      </ng-template>
    </div>
    } } } } @default {
    <div class="cat-assign__stub"></div>
    } }
  </section>

  <!-- ── Selection summary (to add) ─────────────────────────────────────── -->
  <section class="cat-assign__selection" aria-label="Categories to add">
    <div class="cat-assign__selection-head">
      <span>
        Categories to add <strong>({{ toAddList().length }})</strong>
      </span>
      @if (toAddList().length) {
      <button type="button" class="cat-assign__link" (click)="clearSelection()">
        Clear
      </button>
      }
    </div>
    @if (toAddList().length) {
    <ul class="cat-assign__selection-chips">
      @for (s of toAddList(); track trackByShelfId($index, s)) {
      <li class="cat-assign__sel-chip">
        <span class="cat-assign__sel-chip-name">{{ shelfLabel(s) }}</span>
        <button
          type="button"
          class="cat-assign__sel-chip-remove"
          [attr.aria-label]="'Remove ' + shelfLabel(s)"
          (click)="removeFromSelection(s.id)"
        >
          <mat-icon aria-hidden="true">close</mat-icon>
        </button>
      </li>
      }
    </ul>
    } @else {
    <p class="cat-assign__selection-empty">
      Nothing picked yet. Tick categories above to add them to {{ priceCount()
      }} price{{ priceCount() === 1 ? "" : "s" }}.
    </p>
    }
  </section>

  <!-- ── Footer ─────────────────────────────────────────────────────────── -->
  <footer class="cat-assign__footer flex-col">
    @if (liveCopy(); as live) { @if (live.mode === "choose") {
    <div
      class="flex flex-col gap-1.5"
      role="radiogroup"
      aria-labelledby="cat-assign-live-message"
      data-testid="cat-assign-live-choice"
    >
      <p
        id="cat-assign-live-message"
        class="m-0 text-[0.76rem] font-bold text-[#525a68]"
      >
        {{ live.message }}
      </p>
      <div class="flex flex-col gap-0.5">
        <enr-radio
          id="cat-assign-live-keep"
          name="cat-assign-live"
          value="keep"
          [label]="live.options.keep.label"
          [disabled]="saving()"
          [ngModel]="liveChoice()"
          (ngModelChange)="liveChoice.set($event)"
          [attr.data-testid]="'cat-assign-live-keep'"
        />
        <p class="m-0 pl-7 text-[0.72rem] text-[#525a68]">
          {{ live.options.keep.hint }}
        </p>
      </div>
      <div class="flex flex-col gap-0.5">
        <enr-radio
          id="cat-assign-live-off-sale"
          name="cat-assign-live"
          value="offSale"
          [label]="live.options.offSale.label"
          [disabled]="saving()"
          [ngModel]="liveChoice()"
          (ngModelChange)="liveChoice.set($event)"
          [attr.data-testid]="'cat-assign-live-off-sale'"
        />
        <p class="m-0 pl-7 text-[0.72rem] text-[#525a68]">
          {{ live.options.offSale.hint }}
        </p>
      </div>
    </div>
    } @else {
    <p
      class="cat-assign__scope-warn"
      role="status"
      data-testid="cat-assign-live-warning"
    >
      <mat-icon aria-hidden="true">warning</mat-icon>
      {{ live.message }}
    </p>
    } } @if (liveCheck() === "checking") {
    <p
      class="m-0 text-[0.72rem] text-[#525a68]"
      role="status"
      data-testid="cat-assign-live-checking"
    >
      Checking which prices are on sale through another product offering…
    </p>
    } @if (uncheckedCount(); as unchecked) {
    <p
      class="cat-assign__scope-warn"
      role="status"
      data-testid="cat-assign-live-unchecked"
    >
      <mat-icon aria-hidden="true">warning</mat-icon>
      {{ unchecked }} {{ unchecked === 1 ? "price reads" : "prices read" }} In
      design here and may be on sale through another product offering.
    </p>
    } @if (designedCopy(); as designed) {
    <p
      class="m-0 text-[0.72rem] text-[#525a68]"
      role="status"
      data-testid="cat-assign-designed-note"
    >
      {{ designed }}
    </p>
    }
    <div class="flex justify-end gap-2">
      <enr-button variant="text" (clicked)="onCancel()" [disabled]="saving()">
        Cancel
      </enr-button>
      <enr-button
        variant="primary"
        (clicked)="apply()"
        [disabled]="!canApply()"
      >
        @if (saving()) { Assigning… } @else { Assign to {{ priceCount() }}
        price{{ priceCount() === 1 ? "" : "s" }} }
      </enr-button>
    </div>
  </footer>
</div>
